import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";
import { CustomerAuthProvider } from "@/lib/customerAuth";

import HomePage from "@/pages/HomePage";
import RatesPage from "@/pages/RatesPage";
import SubmitPage from "@/pages/SubmitPage";
import SubmittedPage from "@/pages/SubmittedPage";
import TrackPage from "@/pages/TrackPage";
import AcceptedCardsPage from "@/pages/AcceptedCardsPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import FaqPage from "@/pages/FaqPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import NotFoundOrSellBrand from "@/pages/NotFoundOrSellBrand";
import CategoryPage from "@/pages/CategoryPage";
import SignUpPage from "@/pages/SignUpPage";
import SignInPage from "@/pages/SignInPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CustomerDashboardPage from "@/pages/CustomerDashboardPage";
import ReferPage from "@/pages/ReferPage";
import AboutPage from "@/pages/AboutPage";
import PricingPage from "@/pages/PricingPage";

import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminForgotPasswordPage from "@/pages/admin/AdminForgotPasswordPage";
import AdminResetPasswordPage from "@/pages/admin/AdminResetPasswordPage";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminSubmissionsPage from "@/pages/admin/AdminSubmissionsPage";
import AdminSubmissionDetailPage from "@/pages/admin/AdminSubmissionDetailPage";
import AdminCustomersPage from "@/pages/admin/AdminCustomersPage";
import AdminPayoutsPage from "@/pages/admin/AdminPayoutsPage";
import AdminChatPage from "@/pages/admin/AdminChatPage";
import AdminLiveChatPage from "@/pages/admin/AdminLiveChatPage";
import AdminNotificationsPage from "@/pages/admin/AdminNotificationsPage";
import AdminKBPage from "@/pages/admin/AdminKBPage";
import AdminBlogPage from "@/pages/admin/AdminBlogPage";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminVendorReportsPage from "@/pages/admin/AdminVendorReportsPage";
import AdminSupportInboxPage from "@/pages/admin/AdminSupportInboxPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import DomainGuard from "@/components/DomainGuard";

function App() {
  return (
    <DomainGuard>
    <HelmetProvider>
      <CustomerAuthProvider>
        <div className="App">
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Admin */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
              <Route path="/admin/reset" element={<AdminResetPasswordPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminAnalyticsPage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="overview" element={<AdminOverviewPage />} />
                <Route path="submissions" element={<AdminSubmissionsPage />} />
                <Route path="submissions/:id" element={<AdminSubmissionDetailPage />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route path="payouts" element={<AdminPayoutsPage />} />
                <Route path="chat" element={<AdminChatPage />} />
                <Route path="live-chat" element={<AdminLiveChatPage />} />
                <Route path="notifications" element={<AdminNotificationsPage />} />
                <Route path="kb" element={<AdminKBPage />} />
                <Route path="blog" element={<AdminBlogPage />} />
                <Route path="audit" element={<AdminAuditPage />} />
                <Route path="vendor-reports" element={<AdminVendorReportsPage />} />
                <Route path="support-inbox" element={<AdminSupportInboxPage />} />
                <Route path="ai-learning" element={<ComingSoon title="AI Learning" />} />
                <Route path="settings" element={<AdminSettingsPage />} />
              </Route>

              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/rates" element={<RatesPage />} />
                <Route path="/submit" element={<SubmitPage />} />
                <Route path="/submitted" element={<SubmittedPage />} />
                <Route path="/track" element={<TrackPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/forgot-password" element={<ResetPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/dashboard" element={<CustomerDashboardPage />} />
                <Route path="/refer" element={<ReferPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/accepted-cards" element={<AcceptedCardsPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogDetailPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
                <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
                <Route path="/legal/refund" element={<LegalPage kind="refund" />} />
                <Route path="/legal/security" element={<LegalPage kind="security" />} />
                <Route path="/legal/cookies" element={<LegalPage kind="cookies" />} />
                <Route path="/cards/:category" element={<CategoryPage />} />
                <Route path="*" element={<NotFoundOrSellBrand />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </div>
      </CustomerAuthProvider>
    </HelmetProvider>
    </DomainGuard>
  );
}

export default App;

function ComingSoon({ title }) {
  return (
    <div className="px-6 md:px-10 py-16 text-center" data-testid={`coming-soon-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="inline-block px-5 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold tracking-wider uppercase mb-4">
        Coming soon
      </div>
      <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        This module is on the roadmap. The data model is already in place — UI is being built.
      </p>
    </div>
  );
}
